import { WaveformIcon } from '../components/icons';
import type { RecorderApi } from './useRecorder';

/**
 * The take you just made, played back from the local blob.
 *
 * Native controls, deliberately: this is a check that the recording exists and
 * sounds right, not a viewing surface. The custom player lives on the share
 * page, where an audience is watching.
 *
 * The video path relies on the duration header having been patched onto the
 * blob: MediaRecorder does not write one, so without that the scrubber is
 * dead. The audio path gets the same treatment for WebM; Safari's MP4 already
 * carries a duration.
 */
export function TakePreview({ recorder }: { recorder: RecorderApi }) {
  const audio = recorder.mode === 'audio';

  if (!recorder.previewUrl) {
    return (
      <div className="overflow-hidden rounded-md border border-hairline bg-black">
        <div
          className={`flex w-full items-center justify-center text-caption text-chalk-faint ${
            audio ? 'py-14' : 'aspect-video'
          }`}
        >
          No local copy of this take is available to preview.
        </div>
      </div>
    );
  }

  if (audio) {
    return (
      <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
        <div className="clapper-stripes absolute inset-x-0 top-0 h-1.5 opacity-[0.14]" aria-hidden="true" />
        <div className="flex flex-col items-center gap-4 px-5 py-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-hairline bg-slate-850 text-chalk-dim">
            <WaveformIcon size={22} />
          </span>
          <audio src={recorder.previewUrl} controls className="w-full max-w-md" />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-hairline bg-black">
      <video src={recorder.previewUrl} controls playsInline className="aspect-video w-full bg-black" />
    </div>
  );
}
